import ChecklistIcon from "@mui/icons-material/Checklist";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography } from "@mui/material";
import type { GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid, type SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useFacetFilters, useFilterRailHandoff } from "../../../hooks/useFacetFilters";
import { pbjStaffingHoursGridKey } from "../../../services/LocalStorageService";
import { FONT_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import type { PbjShiftManualEditBody, PbjStaffingHoursRow } from "../../../utils/models";
import type { SortingState } from "../../../utils/multiColumnSorting";
import { toTestIdSlug } from "../../../utils/strings";
import { useEditPbjStaffingShift, usePbjStaffingColumnVisibility, usePbjStaffingHours } from "../hooks/usePbjAudit";
import { usePbjJobMapping, usePbjJobs } from "../hooks/usePbjJobs";
import { dataTableSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { hoursColumn } from "../pbjGridCells";
import { pbjSortOptions, sortByOtherField } from "../pbjGridSorting";
import { BulkEditStaffingDialog } from "./BulkEditStaffingDialog";
import { CmsCapBadge } from "./CmsCapBadge";
import { DstBadge } from "./DstBadge";
import { EditableHoverCell } from "./EditableHoverCell";
import { StaffingHoursExpand } from "./StaffingHoursExpand";
import { StaffingHoursFilterRail } from "./StaffingHoursFilterRail";
import { JobSelectEditCell, PayTypeOverrideBadge, RoleStringEditCell, usePayTypeCodeOptions } from "./staffingEditCells";
import { COLUMN_FACET, STAFFING_DIMENSION_LIST, staffingDateOf } from "./staffingHoursFilters";

type Props = {
	startDate: string;
	endDate: string;
};

/** Stable grid id: a row is one (employee, dept-local date, PBJ job). */
const rowId = (r: PbjStaffingHoursRow) => `${r.empId}|${r.date}|${r.pbjJobDescription ?? ""}`;

const SEARCH_WIDTH = { min: 220, max: 360 };

const DEFAULT_SORT: SortingState<PbjStaffingHoursRow>[] = [
	{ sortColumns: ["name"], sortOrder: "asc" },
	{ sortColumns: ["date"], sortOrder: "asc" },
];

/** Fixed detail-row height for the expand sub-grid (header + ~3 rows + py padding). */
const EXPAND_HEIGHT_PX = 220;

/**
 * DAT-209 Staffing Hours — the pbj_shift-backed table in the PBJ Model → Audit tab. One row per
 * (pbj_employee_id, dept-local date, PBJ job); `hours` is worked time net of recorded breaks (read-only).
 *
 * Six metadata/job cells are editable (PBJ job, Job Description, Department Name, Earn Code, Agency
 * Name, Pay Type Code — the latter a manual override over the derived code, flagged with a dot when
 * set). Rows that map 1:1 to a single pbj_shift on one dept-local date (`row.editable`) edit inline
 * with a pencil-on-hover affordance. EVERY row is also expandable to a per-staffing-date breakdown
 * sub-grid (the underlying pbj_shift rows, each editable via the same per-pbj_shift PATCH) — shared
 * infrastructure for future breakdowns (breaks, etc.), not only multi-shift / midnight-split rows.
 *
 * A toolbar "Select rows" toggle enables checkbox selection + a generic bulk edit: one edits-object
 * (any subset of the 6 fields, absent = untouched / null = clear) PATCHed across every selected row's
 * group key — the server resolves aggregated rows to all their contributing shifts.
 */
export const StaffingHoursTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	// Excluded-from-PBJ rows are dropped by the backend (single source of truth in _all_pbj_staffing_rows),
	// so the grid endpoint never returns them; they surface only in the dedicated "Excluded from PBJ" audit
	// tab (the include_excluded=True path). No client-side filter needed — `data` is used directly below.
	const { data, isLoading, isError } = usePbjStaffingHours(startDate, endDate);
	const rows = useMemo(() => data ?? [], [data]);
	// Empeon-only depts never populate EarnCode / Dist Job Code, so hide those columns. While the flag
	// is loading, default to showing them (current behavior) to avoid a flash.
	const { data: columnVisibility } = usePbjStaffingColumnVisibility();
	const hideSourceSpecificColumns = columnVisibility?.hideSourceSpecificColumns ?? false;
	// DEFAULT: hide the Pharos-only Position Code column until the flag loads, so other networks never flash it.
	const showPositionCode = columnVisibility?.showPositionCode ?? false;
	const { data: jobs } = usePbjJobs(departmentId);
	const { data: jobMapping } = usePbjJobMapping(departmentId);
	const editMutation = useEditPbjStaffingShift();
	const payTypeOptions = usePayTypeCodeOptions();
	const facetLabels = useFacetLabels();

	const periodStart = useMemo(() => DateTime.fromISO(startDate), [startDate]);
	const periodEnd = useMemo(() => DateTime.fromISO(endDate), [endDate]);
	const [windowStart, setWindowStart] = useState<DateTime | undefined>(undefined);
	const [windowEnd, setWindowEnd] = useState<DateTime | undefined>(undefined);
	const datesNarrowed = windowStart != null || windowEnd != null;
	// DEFAULT: an unset window edge is the period's own edge — no narrowing.
	const startIso = (windowStart ?? periodStart).toISODate();
	// DEFAULT: see startIso.
	const endIso = (windowEnd ?? periodEnd).toISODate();
	const inDateWindow = useCallback(
		(r: PbjStaffingHoursRow) => {
			const day = staffingDateOf(r).toISODate();
			return startIso != null && endIso != null && day != null && day >= startIso && day <= endIso;
		},
		[startIso, endIso],
	);
	const filters = useFacetFilters(rows, STAFFING_DIMENSION_LIST, inDateWindow);
	const filteredRows = filters.result.rows;
	const activeFilterCount = filters.activeCount + (datesNarrowed ? 1 : 0);
	const clearAllFilters = useCallback(() => {
		filters.clear();
		setWindowStart(undefined);
		setWindowEnd(undefined);
	}, [filters.clear]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: the period bounds are the reset trigger, not read in the body.
	useEffect(() => {
		setWindowStart(undefined);
		setWindowEnd(undefined);
	}, [startDate, endDate]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: departmentId is the reset trigger, not read in the body.
	useEffect(() => filters.clear(), [departmentId, filters.clear]);

	const railHandoff = useFilterRailHandoff(filters.highlight, COLUMN_FACET, t("pbj_model.filter.in_rail"));

	// ── Bulk selection (checkbox rows + one edits-object PATCH across the selection) ──
	const [selectionMode, setSelectionMode] = useState(false);
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
	const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

	const resetSelection = useCallback(() => setRowSelectionModel({ type: "include", ids: new Set() }), []);
	const handleToggleSelectMode = useCallback(() => {
		setSelectionMode((prev) => {
			if (prev) resetSelection();
			return !prev;
		});
	}, [resetSelection]);
	// A department or period switch must never leave a stale selection a bulk edit could target.
	// All three are intentional trigger-only deps.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset triggers, not read in the body.
	useEffect(() => {
		setSelectionMode(false);
		resetSelection();
	}, [departmentId, startDate, endDate, resetSelection]);
	// A bulk edit must only touch rows the analyst can see.
	// biome-ignore lint/correctness/useExhaustiveDependencies: filteredRows is the reset trigger, not read in the body.
	useEffect(() => resetSelection(), [filteredRows, resetSelection]);

	// SystemDataGrid normalizes header select-all to a concrete `include` model, but keep the
	// exclude branch defensive (it resolves against the filtered rows on screen).
	const selectedRows = useMemo<PbjStaffingHoursRow[]>(() => {
		const { type, ids } = rowSelectionModel;
		return filteredRows.filter((r) => (type === "include" ? ids.has(rowId(r)) : !ids.has(rowId(r))));
	}, [filteredRows, rowSelectionModel]);

	const handleBulkApplied = useCallback(() => {
		setSelectionMode(false);
		resetSelection();
	}, [resetSelection]);

	// PBJ job picker: value = job id. "" clears the job. One list labelled by description, one by code
	// (the Pay Title Code column) — both write the same pbjJobId.
	const jobOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => [{ value: "", label: "—" }, ...(jobs ?? []).map((j) => ({ value: j.id, label: `${j.code} - ${j.description}` }))],
		[jobs],
	);
	const codeOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => [{ value: "", label: "—" }, ...(jobs ?? []).map((j) => ({ value: j.id, label: j.code }))],
		[jobs],
	);
	// Job Description (raw_job) is free text, but analysts can also pick a known role string from the
	// Jobs-tab mapping. De-duped + sorted for a stable dropdown.
	const roleStringOptions = useMemo(() => [...new Set((jobMapping ?? []).map((m) => m.roleString))].sort((a, b) => a.localeCompare(b)), [jobMapping]);

	const isCellEditable = useCallback((params: { row: PbjStaffingHoursRow }) => params.row.editable, []);

	const processRowUpdate = useCallback(
		async (newRow: PbjStaffingHoursRow, oldRow: PbjStaffingHoursRow): Promise<PbjStaffingHoursRow> => {
			if (!newRow.editable || !newRow.pbjShiftId) return oldRow;
			const body: PbjShiftManualEditBody = { departmentId };
			let changedField = "";
			if (newRow.pbjJobId !== oldRow.pbjJobId) {
				body.pbjJobId = newRow.pbjJobId || null;
				changedField = "pbjJobId";
			} else if (newRow.jobDescription !== oldRow.jobDescription) {
				body.rawJob = newRow.jobDescription || null;
				changedField = "rawJob";
			} else if (newRow.departmentName !== oldRow.departmentName) {
				body.departmentName = newRow.departmentName || null;
				changedField = "departmentName";
			} else if (newRow.earnCode !== oldRow.earnCode) {
				body.earnCode = newRow.earnCode || null;
				changedField = "earnCode";
			} else if (newRow.agencyName !== oldRow.agencyName) {
				body.agencyName = newRow.agencyName || null;
				changedField = "agencyName";
			} else if (newRow.payTypeCode !== oldRow.payTypeCode) {
				// "1"|"2"|"3" sets a manual override; null clears it (the row reverts to the derived code).
				body.payTypeCode = newRow.payTypeCode ?? null;
				changedField = "payTypeCode";
			} else {
				return newRow;
			}
			// Await the PATCH so a rejection propagates to onProcessRowUpdateError and the grid reverts
			// the cell — the optimistic newRow is only kept once the server accepts the edit. On success
			// the staffing-hours query is invalidated + refetched, replacing newRow with the canonical row.
			await editMutation.mutateAsync({ pbjShiftId: newRow.pbjShiftId, body, changedField });
			return newRow;
		},
		[departmentId, editMutation],
	);

	// The mutation (useEditPbjStaffingShift) already toasts + tracks the failure, so this handler
	// only lets the grid revert the edited cell to its prior value instead of leaving the bad edit
	// on screen. Kept as a distinct callback (not swallowed) so the revert is intentional, not silent.
	const handleProcessRowUpdateError = useCallback((error: unknown) => {
		console.error("Staffing Hours cell edit failed; reverting cell", error);
	}, []);

	const columns: SystemGridColDef<PbjStaffingHoursRow>[] = useMemo(
		() => [
			{ field: "empId", headerName: t("pbj_model.staffing.col.emp_id"), minWidth: 120, flex: 1 },
			{ field: "name", headerName: t("pbj_model.staffing.col.name"), minWidth: 160, flex: 1 },
			{
				field: "jobDescription",
				headerName: t("pbj_model.staffing.col.job_description"),
				minWidth: 160,
				flex: 1,
				editable: true,
				renderEditCell: (params) => <RoleStringEditCell {...params} options={roleStringOptions} />,
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) =>
					params.row.editable ? <EditableHoverCell {...params} display={params.value ?? ""} /> : (params.value ?? ""),
			},
			...(showPositionCode ? [{ field: "distJobCode", headerName: t("pbj_model.staffing.col.position_code"), minWidth: 140 }] : []),
			{
				field: "payTitleCode",
				headerName: t("pbj_model.staffing.col.pay_title_code"),
				minWidth: 140,
				editable: true,
				// Editing the code picks a PBJ job (by code); the change funnels into pbjJobId.
				valueGetter: (_value, r) => r.pbjJobId ?? "",
				valueSetter: (value, r) => ({ ...r, pbjJobId: value === "" ? null : (value as string) }),
				getExportValue: (r) => r.payTitleCode,
				renderEditCell: (params) => <JobSelectEditCell {...params} options={codeOptions} />,
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) => {
					const label = params.row.payTitleCode ?? "";
					return params.row.editable ? <EditableHoverCell {...params} display={label} /> : label;
				},
			},
			{
				field: "payTypeCode",
				headerName: t("pbj_model.staffing.col.pay_type_code"),
				minWidth: 140,
				editable: true,
				// The edit value is the effective code; picking the "—" (derived) option clears the override.
				valueGetter: (_value, r) => r.payTypeCode ?? "",
				valueSetter: (value, r) => ({ ...r, payTypeCode: value === "" ? null : (value as string) }),
				renderEditCell: (params) => <JobSelectEditCell {...params} options={payTypeOptions} />,
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) => {
					const label = params.row.payTypeCode ?? "";
					// Manual overrides get a subtle "manually set" dot; aggregated rows show it too (read-only).
					const badge = params.row.payTypeCodeOverride != null ? <PayTypeOverrideBadge /> : undefined;
					if (params.row.editable) return <EditableHoverCell {...params} display={label} adornment={badge} />;
					if (!badge) return label;
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", justifyContent: "space-between" }}>
							<Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Box>
							{badge}
						</Box>
					);
				},
			},
			{
				field: "pbjJobId",
				headerName: t("pbj_model.staffing.col.pbj_job"),
				minWidth: 200,
				flex: 1,
				editable: true,
				// Coerce null → "" so the picker resolves to the clear ("—") option.
				valueGetter: (_value, r) => r.pbjJobId ?? "",
				getExportValue: (r) => r.pbjJobDescription,
				renderEditCell: (params) => <JobSelectEditCell {...params} options={jobOptions} />,
				// Display the mapped description (existing pbjJobDescription) at rest; the editor uses the id.
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) => {
					const label =
						params.row.pbjJobId == null ? (
							<MetaChip
								tone="warning"
								size="small"
								label={t("pbj_model.staffing.filter.review.no_pbj_job")}
								dataTestId={`pbj-staffing-no-pbj-job-${toTestIdSlug(rowId(params.row))}`}
							/>
						) : (
							params.row.pbjJobDescription
						);
					return params.row.editable ? <EditableHoverCell {...params} display={label} /> : label;
				},
			},
			{
				field: "date",
				headerName: t("pbj_model.staffing.col.date"),
				minWidth: 110,
				// Stored ISO yyyy-mm-dd; CMS bulk-upload format is M/d/yy.
				valueFormatter: (value: string) => (value ? DateTime.fromISO(value).toFormat("M/d/yy") : ""),
				// DST-affected employee-days get an unobtrusive informational marker next to the date. The
				// hours are already correct; the badge only tells the reviewer this day crossed a transition.
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) => {
					const label = params.value ? DateTime.fromISO(params.value as string).toFormat("M/d/yy") : "";
					if (!params.row.dstAffected) return label;
					return <DstBadge label={label} dataTestId="pbj-staffing-dst-badge" />;
				},
			},
			// Keep numeric sorting but left-align header + cell to match the other columns.
			{
				...hoursColumn<PbjStaffingHoursRow>("hours", t("pbj_model.staffing.col.hours"), {
					minWidth: 120,
					align: "left",
					headerAlign: "left",
					// When the employee-day exceeded the CMS 22.5h/day cap, show the FILED (capped) hours with a
					// tooltip citing the real worked hours — matching the submission XML/XLSX. Else show `hours`.
					renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) =>
						params.row.cappedHours == null ? (
							params.row.hours.toFixed(2)
						) : (
							<CmsCapBadge cappedHours={params.row.cappedHours} originalHours={params.row.hours} dataTestId="pbj-staffing-cms-cap-badge" />
						),
				}),
				// DEFAULT: cappedHours is null under the cap, where the filed figure is `hours`.
				getExportValue: (r: PbjStaffingHoursRow) => r.cappedHours ?? r.hours,
			},
			{
				field: "departmentName",
				headerName: t("pbj_model.staffing.col.department_name"),
				minWidth: 170,
				flex: 1,
				editable: true,
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) =>
					params.row.editable ? <EditableHoverCell {...params} display={params.value ?? ""} /> : (params.value ?? ""),
			},
			// EarnCode + Dist Job Code are always NULL for Empeon-only depts, so omit them there.
			...(hideSourceSpecificColumns
				? []
				: [
						{
							field: "earnCode",
							headerName: t("pbj_model.staffing.col.earn_code"),
							minWidth: 100,
							editable: true,
							renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) =>
								params.row.editable ? <EditableHoverCell {...params} display={params.value ?? ""} /> : (params.value ?? ""),
						},
						// DAT-209: per-shift Dist Job Code synced from Paycom (null for Empeon); read-only.
						...(showPositionCode ? [] : [{ field: "distJobCode", headerName: t("pbj_model.staffing.col.dist_job_code"), minWidth: 140 }]),
					]),
			{
				field: "agencyName",
				headerName: t("pbj_model.staffing.col.agency_name"),
				minWidth: 140,
				flex: 1,
				editable: true,
				renderCell: (params: GridRenderCellParams<PbjStaffingHoursRow>) =>
					params.row.editable ? <EditableHoverCell {...params} display={params.value ?? ""} /> : (params.value ?? ""),
			},
			{ field: "w2OrContractor", headerName: t("pbj_model.staffing.col.w2_or_1099"), minWidth: 100 },
			{
				field: "employmentType",
				headerName: t("pbj_model.staffing.col.employment_type"),
				minWidth: 130,
				valueFormatter: (value: string | null) => (value == null ? "" : facetLabels.employmentType(value)),
			},
		],
		[t, jobOptions, codeOptions, roleStringOptions, payTypeOptions, hideSourceSpecificColumns, showPositionCode, facetLabels],
	);

	const sortingOptions = useMemo(
		() => [
			...pbjSortOptions<PbjStaffingHoursRow>([
				{ field: "empId", label: t("pbj_model.staffing.col.emp_id") },
				{ field: "name", label: t("pbj_model.staffing.col.name") },
				{ field: "jobDescription", label: t("pbj_model.staffing.col.job_description") },
				...(showPositionCode ? [{ field: "distJobCode" as const, label: t("pbj_model.staffing.col.position_code") }] : []),
				{ field: "payTitleCode", label: t("pbj_model.staffing.col.pay_title_code") },
				{ field: "payTypeCode", label: t("pbj_model.staffing.col.pay_type_code") },
				{ field: "date", label: t("pbj_model.staffing.col.date") },
				{ field: "hours", label: t("pbj_model.staffing.col.hours") },
				{ field: "departmentName", label: t("pbj_model.staffing.col.department_name") },
				...(hideSourceSpecificColumns
					? []
					: [
							{ field: "earnCode" as const, label: t("pbj_model.staffing.col.earn_code") },
							...(showPositionCode ? [] : [{ field: "distJobCode" as const, label: t("pbj_model.staffing.col.dist_job_code") }]),
						]),
				{ field: "agencyName", label: t("pbj_model.staffing.col.agency_name") },
				{ field: "w2OrContractor", label: t("pbj_model.staffing.col.w2_or_1099") },
				{ field: "employmentType", label: t("pbj_model.staffing.col.employment_type") },
			]),
			sortByOtherField<PbjStaffingHoursRow>("pbjJobId", t("pbj_model.staffing.col.pbj_job"), (r) => r.pbjJobDescription),
		],
		[t, hideSourceSpecificColumns, showPositionCode],
	);

	// Every row is expandable to its per-staffing-date underlying-shift breakdown — shared
	// infrastructure for upcoming breakdowns (breaks, etc.), not just multi-shift / midnight-split rows.
	// 1:1 rows still also edit inline; the expand simply shows the single contributing shift.
	const getRowExpandContent = useCallback(
		(row: PbjStaffingHoursRow) => (
			<StaffingHoursExpand row={row} departmentId={departmentId} jobOptions={jobOptions} codeOptions={codeOptions} roleStringOptions={roleStringOptions} />
		),
		[departmentId, jobOptions, codeOptions, roleStringOptions],
	);

	// Selection-mode toggle + the bulk action live in the grid toolbar (Team.tsx pattern): a single
	// "Select rows" affordance flips to [Edit N selected · Cancel] while selecting.
	const selectionToolbar = selectionMode ? (
		// A cohesive "selection bar" so the controls read as one contextual unit, not floating buttons.
		// Empty state shows an instructive prompt (no dead "Edit 0 selected" CTA); the primary action
		// appears only once there are rows to act on.
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				ml: "auto",
				pl: selectedRows.length === 0 ? 1.5 : 0.5,
				pr: 0.5,
				py: 0.5,
				borderRadius: 1.5,
				backgroundColor: NEUTRAL[50],
				border: `1px solid ${NEUTRAL[200]}`,
			}}
		>
			{selectedRows.length === 0 ? (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.MUTED, whiteSpace: "nowrap", mr: 0.5 }}>
					{t("pbj_model.staffing.bulk.select_prompt")}
				</Typography>
			) : (
				<SystemButton
					buttonVariant="primary"
					size="small"
					onClick={() => setBulkDialogOpen(true)}
					noFullWidthMobile
					data-testid="pbj-staffing-bulk-edit-button"
				>
					{t("pbj_model.staffing.bulk.edit_selected", { num: selectedRows.length })}
				</SystemButton>
			)}
			<SystemButton
				buttonVariant="borderless"
				size="small"
				onClick={handleToggleSelectMode}
				leadingIcon={<CloseIcon sx={{ fontSize: 18 }} />}
				noFullWidthMobile
				data-testid="pbj-staffing-select-cancel"
			>
				{t("cancel")}
			</SystemButton>
		</Box>
	) : (
		<Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
			<SystemButton
				buttonVariant="secondary"
				size="small"
				onClick={handleToggleSelectMode}
				leadingIcon={<ChecklistIcon sx={{ fontSize: 18 }} />}
				noFullWidthMobile
				data-testid="pbj-staffing-select-toggle"
			>
				{t("pbj_model.staffing.bulk.select_rows")}
			</SystemButton>
		</Box>
	);

	return (
		<>
			{/* Fills the sub-tab's height so the grid, not the page, scrolls. */}
			<FilterRailFrame
				flat
				dataTestId="pbj-model-staffing-hours-frame"
				rail={
					<StaffingHoursFilterRail
						rows={rows}
						filters={filters}
						activeFilterCount={activeFilterCount}
						onClearAll={clearAllFilters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						hideSourceSpecificColumns={hideSourceSpecificColumns}
						periodStart={periodStart}
						periodEnd={periodEnd}
						windowStart={windowStart}
						windowEnd={windowEnd}
						onWindowStartChange={setWindowStart}
						onWindowEndChange={setWindowEnd}
					/>
				}
			>
				<SystemDataGrid<PbjStaffingHoursRow>
					rows={filteredRows}
					columns={columns}
					getRowId={rowId}
					searchableColumns={["empId", "name", "jobDescription", "pbjJobDescription", "agencyName"]}
					searchPlaceholder={t("pbj_model.staffing.search")}
					searchWidth={SEARCH_WIDTH}
					loading={isLoading}
					error={isError}
					emptyState={
						<FilteredEmptyState
							filtered={activeFilterCount > 0}
							onClearFilters={clearAllFilters}
							unfilteredState={<EmptyState title={t("pbj_model.staffing.empty")} dataTestId="pbj-model-staffing-hours-empty" />}
							dataTestId="pbj-model-staffing-hours-filtered-empty"
						/>
					}
					sortingOptions={sortingOptions}
					showSortSummary
					defaultSortState={DEFAULT_SORT}
					dropUnofferedSorts
					localStorageKey={pbjStaffingHoursGridKey}
					columnPicker={{}}
					enableColumnReorder
					persistColumnWidths
					keepToolbarControlsTogether
					exportOptions={{ fileName: t("pbj_model.staffing.export_file_name") }}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					isCellEditable={isCellEditable}
					processRowUpdate={processRowUpdate}
					onProcessRowUpdateError={handleProcessRowUpdateError}
					getRowExpandContent={getRowExpandContent}
					getExpandContentHeight={() => EXPAND_HEIGHT_PX}
					trailingToolbarNodes={selectionToolbar}
					defaultPageSize={50}
					dataGridProps={{
						checkboxSelection: selectionMode,
						rowSelectionModel,
						onRowSelectionModelChange: setRowSelectionModel,
					}}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-model-staffing-hours-table"
				/>
			</FilterRailFrame>
			<BulkEditStaffingDialog
				open={bulkDialogOpen}
				onClose={() => setBulkDialogOpen(false)}
				selectedRows={selectedRows}
				jobOptions={jobOptions}
				hideSourceSpecificColumns={hideSourceSpecificColumns}
				onApplied={handleBulkApplied}
			/>
		</>
	);
};
